import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { CheckCircle, XCircle, Eye, Download, CreditCard } from 'lucide-react'; 
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import '../styles/BillingScreen.css'; 
import RecordPaymentModal from '../components/billing/RecordPaymentModal';

const generateInvoicePDF = (invoice, subscriptions) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos;
    
    // Brand Colors
    const navy = [13, 31, 60];
    const cyan = [23, 162, 184];
    const white = [255, 255, 255];
    const gray = [100, 116, 139];
    
    // ========== HEADER ==========
    doc.setFillColor(...navy);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setFillColor(...cyan);
    doc.triangle(0, 32, pageWidth * 0.3, 32, 0, 50, 'F');
    doc.triangle(pageWidth * 0.7, 32, pageWidth, 32, pageWidth, 40, 'F');
    
    // Company Logo
    const logoImg = new Image();
    logoImg.src = '/theWesterntech.png';
    try {
        doc.addImage(logoImg, 'PNG', 20, 8, 35, 22);
    } catch (e) {}
    
    // Invoice number
    doc.setFontSize(9);
    doc.setTextColor(...white);
    doc.setFont(undefined, 'normal');
    doc.text(`NO: INV-${String(invoice.id).padStart(5, '0')}-1`, pageWidth - 20, 18, { align: 'right' });
    
    // Generated date right below invoice number
    const generatedDate = new Date(invoice.created_at).toLocaleDateString('en-US', {
        day: 'numeric', month: 'short', year: 'numeric'
    });
    doc.setFontSize(7);
    doc.setTextColor(...cyan);
    doc.text(generatedDate, pageWidth - 20, 25, { align: 'right' });
    
    // ========== BILL TO & FROM ==========
    yPos = 52;
    
    // Bill To
    doc.setFontSize(9);
    doc.setTextColor(...navy);
    doc.setFont(undefined, 'bold');
    doc.text('Bill To:', 20, yPos);
    
    doc.setFontSize(8);
    doc.setTextColor(51, 51, 51);
    doc.setFont(undefined, 'normal');
    doc.text(invoice.customer_name || 'N/A', 20, yPos + 7);
    
    doc.setFontSize(7);
    doc.setTextColor(...gray);
    doc.text(invoice.property_name || '', 20, yPos + 13);
    
    // Property address (only if exists and not empty)
    if (invoice.property_address && invoice.property_address.trim()) {
        doc.text(invoice.property_address, 20, yPos + 19);
    }
    
    // From
    doc.setFontSize(9);
    doc.setTextColor(...navy);
    doc.setFont(undefined, 'bold');
    doc.text('From:', pageWidth - 20, yPos, { align: 'right' });
    
    doc.setFontSize(8);
    doc.setTextColor(51, 51, 51);
    doc.setFont(undefined, 'normal');
    doc.text('The WesternTech', pageWidth - 20, yPos + 7, { align: 'right' });
    
    // ========== SERVICE PERIOD (highlighted) ==========
    yPos += 22;
    
    const invoiceSubscriptions = subscriptions.filter(s => 
        invoice.subscription_ids?.includes(s.id) || 
        invoice.property_name === s.property_name
    );
    const billingCycle = invoiceSubscriptions.length > 0 ? invoiceSubscriptions[0].billing_cycle : 'MONTHLY';
    
    const startDate = new Date(invoice.created_at);
    const endDate = new Date(startDate);
    if (billingCycle === 'ANNUALLY') {
        endDate.setFullYear(endDate.getFullYear() + 1);
    } else {
        endDate.setMonth(endDate.getMonth() + 1);
    }
    
    const serviceStart = startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    const serviceEnd = endDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    
    // Service period bar
    doc.setFillColor(...cyan);
    doc.rect(20, yPos, pageWidth - 40, 10, 'F');
    
    doc.setFontSize(8);
    doc.setTextColor(...white);
    doc.setFont(undefined, 'bold');
    doc.text(`SERVICE PERIOD: ${serviceStart} - ${serviceEnd}`, pageWidth / 2, yPos + 7, { align: 'center' });
    
    // ========== TABLE ==========
    yPos += 18;
    
    // Table Header
    doc.setFillColor(...navy);
    doc.rect(20, yPos, pageWidth - 40, 8, 'F');
    
    doc.setFontSize(7);
    doc.setTextColor(...white);
    doc.setFont(undefined, 'bold');
    doc.text('Description', 25, yPos + 6);
    doc.text('Qty', 100, yPos + 6, { align: 'center' });
    doc.text('Price', 125, yPos + 6, { align: 'center' });
    doc.text('Total', pageWidth - 25, yPos + 6, { align: 'right' });
    
    // Table Rows
    yPos += 10;
    doc.setFontSize(7);
    doc.setTextColor(51, 51, 51);
    doc.setFont(undefined, 'normal');
    
    if (invoiceSubscriptions.length > 0) {
        invoiceSubscriptions.forEach((sub, index) => {
            if (index % 2 === 0) {
                doc.setFillColor(248, 250, 252);
                doc.rect(20, yPos - 3, pageWidth - 40, 8, 'F');
            }
            
            doc.text(sub.product_name, 25, yPos + 3);
            doc.text('1', 100, yPos + 3, { align: 'center' });
            doc.text(`$${parseFloat(sub.price).toFixed(2)}`, 125, yPos + 3, { align: 'center' });
            doc.text(`$${parseFloat(sub.price).toFixed(2)}`, pageWidth - 25, yPos + 3, { align: 'right' });
            
            yPos += 8;
        });
    } else {
        doc.text('Service Subscription', 25, yPos + 3);
        doc.text('1', 100, yPos + 3, { align: 'center' });
        doc.text(`$${parseFloat(invoice.amount_due).toFixed(2)}`, 125, yPos + 3, { align: 'center' });
        doc.text(`$${parseFloat(invoice.amount_due).toFixed(2)}`, pageWidth - 25, yPos + 3, { align: 'right' });
        yPos += 8;
    }
    
    // Table border
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(20, yPos, pageWidth - 20, yPos);
    
    // ========== SUBTOTAL ==========
    yPos += 5;
    
    doc.setFillColor(...navy);
    doc.rect(pageWidth - 65, yPos, 45, 8, 'F');
    
    doc.setFontSize(7);
    doc.setTextColor(...white);
    doc.setFont(undefined, 'bold');
    doc.text('Sub Total', pageWidth - 60, yPos + 6);
    doc.text(`$${parseFloat(invoice.amount_due).toFixed(2)}`, pageWidth - 25, yPos + 6, { align: 'right' });
    
    // ========== FOOTER ==========
    const footerY = pageHeight - 20;
    
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(20, footerY, pageWidth - 20, footerY);
    
    doc.setFontSize(6);
    doc.setTextColor(...gray);
    doc.setFont(undefined, 'normal');
    doc.text('The WesternTech | thewesterntech.com', 20, footerY + 6);
    
    return doc;
};

const BillingScreen = ({ user }) => {
    const [activeTab, setActiveTab] = useState('subscriptions');
    const [invoices, setInvoices] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState('all');
    const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
    const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, selectedInvoice: null });
    const [cancelModalState, setCancelModalState] = useState({ isOpen: false, selectedSubscription: null });
    const [cancellationDate, setCancellationDate] = useState('');

    const fetchProperties = async () => {
        try {
            const allProperties = await api.getPropertiesByFeature('billing');
            setProperties(allProperties);
        } catch (err) {
            console.error('Failed to fetch properties:', err);
        }
    };

    const fetchInvoices = async () => {
        try {
            const allInvoices = await api.getInvoices();
            setInvoices(allInvoices);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchSubscriptions = async () => {
        try {
            const allSubscriptions = await api.getAllSubscriptions();
            setSubscriptions(allSubscriptions);
        } catch (err) {
            setError(err.message);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        Promise.all([fetchProperties(), fetchInvoices(), fetchSubscriptions()])
            .finally(() => setIsLoading(false));
    }, []);

    const handleRecordPayment = async (formData) => {
        const { selectedInvoice } = modalState;
        await api.recordInvoicePayment(selectedInvoice.id, formData);
        fetchInvoices();
    };

    const handleCloseModal = () => {
        setModalState({ isOpen: false, selectedInvoice: null });
    };

    const handleOpenModal = (invoice) => {
        setModalState({ isOpen: true, selectedInvoice: invoice });
    };

    const handleOpenCancelModal = (subscription) => {
        const today = new Date().toISOString().split('T')[0];
        setCancellationDate(today);
        setCancelModalState({ isOpen: true, selectedSubscription: subscription });
    };

    const handleCloseCancelModal = () => {
        setCancelModalState({ isOpen: false, selectedSubscription: null });
        setCancellationDate('');
    };

    const handleCancelSubscription = async () => {
        const { selectedSubscription } = cancelModalState;
        if (!selectedSubscription || !cancellationDate) return;

        const toastId = toast.loading('Cancelling subscription...');
        try {
            await api.cancelSubscription(selectedSubscription.id, cancellationDate);
            toast.success('Subscription cancelled successfully.', { id: toastId });
            handleCloseCancelModal();
            fetchSubscriptions();
        } catch (err) {
            toast.error(err.message || 'Failed to cancel subscription.', { id: toastId });
        }
    };

    const handleViewInvoice = (invoice) => {
        const doc = generateInvoicePDF(invoice, subscriptions);
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    };

    const handleDownloadInvoice = (invoice) => {
        const doc = generateInvoicePDF(invoice, subscriptions);
        doc.save(`invoice-${invoice.id}.pdf`);
    };

    // Filter subscriptions
    const filteredSubscriptions = subscriptions.filter(sub => {
        if (selectedPropertyId !== 'all' && String(sub.property_id) !== String(selectedPropertyId)) return false;
        if (subscriptionStatusFilter !== 'all' && sub.status !== subscriptionStatusFilter) return false;
        return true;
    });
    
    // Filter invoices
    const filteredInvoices = invoices.filter(inv => {
        if (selectedPropertyId !== 'all' && String(inv.property_id) !== String(selectedPropertyId)) return false;
        if (invoiceStatusFilter !== 'all' && inv.status !== invoiceStatusFilter) return false;
        return true;
    });

    // Calculate summary for selected property (or all)
    const totalPaid = filteredInvoices
        .filter(inv => inv.status === 'PAID')
        .reduce((sum, inv) => sum + parseFloat(inv.amount_due || 0), 0);

    const totalPending = filteredInvoices
        .filter(inv => inv.status === 'OPEN')
        .reduce((sum, inv) => sum + parseFloat(inv.amount_due || 0), 0);

    const activeSubscriptions = filteredSubscriptions.filter(sub => sub.status === 'ACTIVE');

    const nextBillingDate = activeSubscriptions.length > 0
        ? activeSubscriptions.reduce((earliest, sub) => {
            const date = new Date(sub.next_billing_date);
            return date < earliest ? date : earliest;
        }, new Date(Math.max(...activeSubscriptions.map(sub => new Date(sub.next_billing_date)))))
        : null;

    const pendingInvoiceCount = filteredInvoices.filter(inv => inv.status === 'OPEN').length;
    const propertiesWithPending = selectedPropertyId === 'all'
        ? [...new Set(invoices.filter(inv => inv.status === 'OPEN').map(inv => inv.property_id))].length
        : 0;
    
    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">Billing</h1>
            </div>

            <div className="billing-filters">
                <div className="filter-group">
                    <label className="filter-label">Property</label>
                    <select
                        className="filter-select"
                        value={selectedPropertyId}
                        onChange={(e) => setSelectedPropertyId(e.target.value)}
                    >
                        <option value="all">All Properties</option>
                        {properties.map(prop => (
                            <option key={prop.id} value={prop.id}>{prop.name}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label className="filter-label">Invoice Status</label>
                    <select
                        className="filter-select"
                        value={invoiceStatusFilter}
                        onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="OPEN">Pending</option>
                        <option value="PAID">Paid</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label className="filter-label">Subscription Status</label>
                    <select
                        className="filter-select"
                        value={subscriptionStatusFilter}
                        onChange={(e) => setSubscriptionStatusFilter(e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="ACTIVE">Active</option>
                        <option value="CANCELED">Canceled</option>
                    </select>
                </div>
            </div>

            <div className="billing-summary">
                <div className="summary-card">
                    <span className="summary-label">Total Paid</span>
                    <span className="summary-value summary-paid">${totalPaid.toFixed(2)}</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Pending</span>
                    <span className="summary-value summary-pending">${totalPending.toFixed(2)}</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Active Subscriptions</span>
                    <span className="summary-value">{activeSubscriptions.length}</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">
                        {selectedPropertyId === 'all' ? 'Pending Invoices' : 'Next Billing'}
                    </span>
                    <span className="summary-value">
                        {selectedPropertyId === 'all' 
                            ? pendingInvoiceCount
                            : (nextBillingDate ? nextBillingDate.toLocaleDateString() : '—')
                        }
                    </span>
                </div>
            </div>

            <div className="detail-tabs">
                <button className={`tab-button ${activeTab === 'subscriptions' ? 'active' : ''}`} onClick={() => setActiveTab('subscriptions')}>Subscriptions</button>
                <button className={`tab-button ${activeTab === 'invoices' ? 'active' : ''}`} onClick={() => setActiveTab('invoices')}>Invoices</button>
            </div>

            <div className="card">
                {isLoading && <div className="placeholder-text">Loading...</div>}
                {error && <p className="error-message">Error: {error}</p>}
                
                {!isLoading && activeTab === 'subscriptions' && (
                    <>
                        {filteredSubscriptions.length === 0 && (
                            <div className="placeholder-text">No subscriptions found.</div>
                        )}
                        {filteredSubscriptions.length > 0 && (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Customer</th>
                                        <th>Product</th>
                                        <th>Status</th>
                                        <th>Start Date</th>
                                        <th>Next Billing</th>
                                        <th>Price</th>
                                        <th>Domain</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSubscriptions.map(sub => (
                                        <tr key={sub.id}>
                                            <td data-label="Property">{sub.property_name || 'N/A'}</td>
                                            <td data-label="Customer">{sub.customer_name}</td>
                                            <td data-label="Product">{sub.product_name}</td>
                                            <td data-label="Status">
                                                <span className={`status-badge status-${sub.status.toLowerCase()}`}>
                                                    {sub.status}
                                                </span>
                                            </td>
                                            <td data-label="Start Date">{new Date(sub.start_date).toLocaleDateString()}</td>
                                            <td data-label="Next Billing">{new Date(sub.next_billing_date).toLocaleDateString()}</td>
                                            <td data-label="Price">${parseFloat(sub.price).toFixed(2)}/{sub.billing_cycle === 'ANNUALLY' ? 'yr' : 'mo'}</td>
                                            <td data-label="Domain">{sub.domain_name || '-'}</td>
                                            <td data-label="Actions">
                                                <div className="action-buttons">
                                                    {user.role === 'SuperAdmin' && sub.status === 'ACTIVE' ? (
                                                        <button 
                                                            className="action-btn cancel"
                                                            onClick={() => handleOpenCancelModal(sub)}
                                                            title="Cancel Subscription"
                                                        >
                                                            <XCircle size={14} />
                                                        </button>
                                                    ) : (
                                                        <span className={`status-badge status-${sub.status.toLowerCase()}`}>
                                                            {sub.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                )}

                {!isLoading && activeTab === 'invoices' && (
                    <>
                        {filteredInvoices.length === 0 && (
                            <div className="placeholder-text">No invoices found.</div>
                        )}
                        {filteredInvoices.length > 0 && (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Invoice ID</th>
                                        <th>Customer</th>
                                        <th>Property</th>
                                        <th>Status</th>
                                        <th>Date Issued</th>
                                        <th>Amount Due</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredInvoices.map(invoice => (
                                        <tr key={invoice.id}>
                                            <td data-label="Invoice ID">#{invoice.id}</td>
                                            <td data-label="Customer">{invoice.customer_name}</td>
                                            <td data-label="Property">{invoice.property_name || 'N/A'}</td>
                                            <td data-label="Status">
                                                <span className={`status-badge status-${invoice.status.toLowerCase()}`}>
                                                    {invoice.status}
                                                </span>
                                            </td>
                                            <td data-label="Date Issued">{new Date(invoice.created_at).toLocaleDateString()}</td>
                                            <td data-label="Amount Due">${parseFloat(invoice.amount_due).toFixed(2)}</td>
                                            <td data-label="Actions">
                                                <div className="action-buttons">
                                                    <button 
                                                        className="action-btn view"
                                                        onClick={() => handleViewInvoice(invoice)}
                                                        title="View Invoice"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    <button 
                                                        className="action-btn download"
                                                        onClick={() => handleDownloadInvoice(invoice)}
                                                        title="Download Invoice"
                                                    >
                                                        <Download size={14} />
                                                    </button>
                                                    {user.role === 'SuperAdmin' && invoice.status === 'OPEN' && (
                                                        <button 
                                                            className="action-btn payment"
                                                            onClick={() => handleOpenModal(invoice)}
                                                            title="Record Payment"
                                                        >
                                                            <CreditCard size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                )}
            </div>

            <RecordPaymentModal
                isOpen={modalState.isOpen}
                onClose={handleCloseModal}
                onSave={handleRecordPayment}
                invoice={modalState.selectedInvoice}
            />

            {/* Cancel Subscription Modal */}
            {cancelModalState.isOpen && cancelModalState.selectedSubscription && (
                <div className="modal-overlay">
                    <div className="modal-content modal-content-cancel">
                        <h2>Cancel Subscription</h2>
                        <p className="modal-subtitle">
                            Are you sure you want to cancel this subscription?
                        </p>
                        
                        <div className="cancel-details">
                            <div className="detail-row">
                                <span className="detail-label">Property:</span>
                                <span className="detail-value">{cancelModalState.selectedSubscription.property_name}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Product:</span>
                                <span className="detail-value">{cancelModalState.selectedSubscription.product_name}</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Cancellation Date</label>
                            <input 
                                type="date"
                                className="form-input"
                                value={cancellationDate}
                                onChange={(e) => setCancellationDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        <div className="modal-actions">
                            <button 
                                type="button" 
                                className="button button-secondary" 
                                onClick={handleCloseCancelModal}
                            >
                                Keep Active
                            </button>
                            <button 
                                type="button" 
                                className="button button-cancel-confirm"
                                onClick={handleCancelSubscription}
                                disabled={!cancellationDate}
                            >
                                Cancel Subscription
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingScreen;
